import { useEffect, useRef, useState } from 'react';

export interface InsightDaCena {
    id: string;
    grupo: string;
    texto: string;
}

interface PalcoDoBossFinalProps {
    insights: InsightDaCena[];
    aoFechar: () => void;
}

/**
 * Tela cheia do Boss Final: a cena Phaser (carro montado com os destaques) roda aqui. Abre na tela verde com
 * a logo, e cada Enter libera um passo: o primeiro insight, o segundo, ... e, depois do último, o carro. O Phaser é
 * carregado só neste momento, e o jogo é desmontado ao fechar. No fim da animação o carro fica na
 * tela e o botão de fechar ganha destaque.
 */
export function PalcoDoBossFinal({ insights, aoFechar }: PalcoDoBossFinalProps) {
    const alvo = useRef<HTMLDivElement | null>(null);
    const [terminou, setTerminou] = useState(false);
    const [falhou, setFalhou] = useState(false);
    const jogoRef = useRef<{ avancar?: () => void } | null>(null);

    useEffect(() => {
        let cancelado = false;
        let jogo: { destruir: () => void; avancar?: () => void } | null = null;
        import('../cena/iniciarJogo.js')
            .then(({ iniciarJogoDoBossFinal }) => {
                if (cancelado || !alvo.current) return;
                jogo = iniciarJogoDoBossFinal({
                    parent: alvo.current,
                    insights,
                    aoFimDaAnimacao: () => setTerminou(true),
                    aoFechar,
                });
                jogoRef.current = jogo;
            })
            .catch(() => {
                if (!cancelado) setFalhou(true);
            });
        return () => {
            cancelado = true;
            jogoRef.current = null;
            jogo?.destruir();
        };
    }, [insights, aoFechar]);

    useEffect(() => {
        const aoTeclar = (evento: KeyboardEvent) => {
            if (evento.key === 'Escape') aoFechar();
            if (evento.key === 'Enter') {
                // Enter conduz a animação; sem o preventDefault ele também acionaria o botão com foco.
                evento.preventDefault();
                if (!evento.repeat) jogoRef.current?.avancar?.();
            }
        };
        window.addEventListener('keydown', aoTeclar, true);
        return () => window.removeEventListener('keydown', aoTeclar, true);
    }, [aoFechar]);

    return (
        <div className="palco-boss-final" role="dialog" aria-modal="true" aria-label="Boss Final">
            <div ref={alvo} className="palco-boss-final-tela" data-testid="palco-boss-final" />
            {falhou && <p className="palco-boss-final-erro" role="alert">Não foi possível carregar a animação.</p>}
            {/* No fim da animação quem fecha é o FECHAR da própria cena, sob a logo (como no jogo). */}
            {!terminou && (
                <button type="button" className="palco-boss-final-fechar" onClick={aoFechar}>
                    Encerrar animação
                </button>
            )}
        </div>
    );
}
